import React from 'react';
import { ArrowLeft, Users, Target, TrendingUp, Award, Calendar, BookOpen, CheckCircle, Heart, Play, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { dcMenInECE } from '../data/programs';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DCMenInECE = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section id="main-content" role="main" tabIndex={-1} className="relative pt-20 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <Link to="/" className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src={dcMenInECE.logo} 
                alt="DC Men in ECE Logo" 
                className="w-48 h-48 lg:w-64 lg:h-64 object-contain bg-white rounded-2xl p-4 shadow-2xl"
              />
            </div>
            
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <span className="inline-block px-4 py-2 bg-teal-500/20 text-teal-300 rounded-full text-sm font-semibold mb-6">
                DCELC Program
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                {dcMenInECE.title}
              </h1>
              <p className="text-xl text-teal-200 mb-4">
                {dcMenInECE.subtitle}
              </p>
              <p className="text-lg text-white/70 mb-6">
                Recruitment • Retention • Advancement
              </p>
              <a 
                href="https://www.dcmeninece.org" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 text-lg">
                  Visit Official Website
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {dcMenInECE.stats.map((stat, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-slate-50">
                <p className="text-3xl font-bold text-teal-600 mb-2">{stat.value}</p>
                <p className="text-sm text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conference Photo Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
              Our Community
            </span>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">DC Men in ECE Conference 2023</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From an initial networking reception of 40 men to hosting a city-wide conference with over 200 registrants
            </p>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img
              src={dcMenInECE.heroImage}
              alt="DC Men in ECE Conference 2023"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-slate-700 leading-relaxed">
            {dcMenInECE.intro}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Target className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-6">{dcMenInECE.mission.title}</h2>
            <p className="text-xl text-slate-700 italic leading-relaxed">
              "{dcMenInECE.mission.description}"
            </p>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">{dcMenInECE.impact.title}</h2>
              <p className="text-slate-600 mb-8">{dcMenInECE.impact.description}</p>
              <ul className="space-y-4">
                {dcMenInECE.impact.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-800 mb-6">{dcMenInECE.benefits.title}</h3>
              <ul className="space-y-4">
                {dcMenInECE.benefits.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Program Objectives */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">Program Objectives and Activities</h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Building on a city-wide needs assessment and national best practices, DC Men in ECE's program centers on three interrelated aims:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {dcMenInECE.objectives.map((objective, index) => {
              const icons = [BookOpen, Heart, Award];
              const Icon = icons[index];
              const colors = ['teal', 'emerald', 'amber'];
              const color = colors[index];
              
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                  <div className={`w-14 h-14 bg-${color}-100 rounded-xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-7 h-7 text-${color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{objective.title}</h3>
                  <p className="text-slate-600 mb-6">{objective.description}</p>
                  <ul className="space-y-3">
                    {objective.activities.map((activity, actIndex) => (
                      <li key={actIndex} className="text-sm text-slate-600 pl-4 border-l-2 border-teal-200">
                        {activity}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Conference Section */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl font-bold mb-6">{dcMenInECE.conference.title}</h2>
          <p className="text-lg text-teal-100 leading-relaxed">
            {dcMenInECE.conference.description}
          </p>
        </div>
      </section>

      {/* Promo Video Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Play className="w-8 h-8 text-teal-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Watch Our Promo Video</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Learn more about DC Men in ECE and our mission to empower men of color in early childhood education
            </p>
          </div>
          
          {/* Video Player Container */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video
              controls
              className="w-full h-auto"
              poster=""
              preload="metadata"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/ydyysqx2_%5BAPPROVED%5DDCELC_DC%20MEN%20IN%20ECE%2025%27%20CON_FINAL_V02.mp4" 
                type="video/mp4" 
              />
              <track
                kind="captions"
                src="/captions/dc-men-in-ece.vtt"
                srcLang="en"
                label="English"
                default
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">{dcMenInECE.outcomes.title}</h2>
          <p className="text-slate-600 text-center mb-12">{dcMenInECE.outcomes.description}</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {dcMenInECE.outcomes.metrics.map((metric, index) => (
              <div key={index} className="p-6 bg-slate-50 rounded-xl border border-slate-200">
                <CheckCircle className="w-8 h-8 text-teal-500 mb-4" />
                <p className="text-slate-700">{metric}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-slate-600 italic">{dcMenInECE.outcomes.methods}</p>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">Strategic Partners</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {dcMenInECE.partners.map((partner, index) => (
              <span key={index} className="px-6 py-3 bg-white rounded-full text-slate-700 shadow-sm border border-slate-200">
                {partner}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-slate-700 leading-relaxed mb-8">
            {dcMenInECE.closing}
          </p>
          <Button
            onClick={() => window.location.href = '/#contact'}
            className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white px-8 py-6 text-lg"
          >
            Get Involved
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DCMenInECE;
